/*
 * Copyright (c) 2021 LucidResolutions LLC. All rights reserved.
 */


import {exchangeTypeLookup} from "./exchange.service";
import {ExternalApiBase} from "./external.api.base.service";
import {OrderBook, OrderBookSide} from "../lib/orderbook/orderbook";
import {TraderService} from "./trader.service";
import BigNumber from "bignumber.js";
import EventEmitter from "events";

const BinanceUS = "binanceus"
const Gemini = "gemini"
const CoinbasePro = "coinbasepro"
const Kraken = "kraken"
const tickerEventType = "tickerSubscribe"

let geminiAPI = (new ExternalApiBase('https://api.gemini.com')).useAllAsJson(true)
let coinbaseApi = (new ExternalApiBase('https://api.pro.coinbase.com')).useAllAsJson(true)

export class TickerService {
    sockets: Record<string, WebSocket> = {}
    tickers: any = {}
    initialized: any = {}
    emitters: Record<string, EventEmitter> = {};
    private traderSVC: any;
    constructor() {
    }

    withTraderService(traderSVC: TraderService){
        this.traderSVC = traderSVC;
        let exchanges = [BinanceUS, Gemini, CoinbasePro, Kraken]
        exchanges.forEach((exchange)=>{
            this.emitters[exchange] = new EventEmitter()
        })


    }

    toSymbol(symbol: any) {
        return symbol.toUpperCase().replace(/[^a-z0-9]/ig, '')
    }
    async tickerFor(exchangeID: string, symbol: any) {
        let t = exchangeTypeLookup[exchangeID]
        return new Promise((resolve, reject)=>{
            let check = ()=>{
                this.emitters?.[t]?.emit(tickerEventType, symbol)
                let ticker = this.tickers?.[t]?.[symbol]
                if(ticker){
                    return {
                        symbol: symbol,
                        ticker: ticker
                    }
                }

                return null
            }

            let ticker = check()
            if(ticker){
                resolve(ticker)
                return
            }

            let interval = setInterval(()=>{
                let ticker = check()
                if(ticker){
                    resolve(ticker)
                    clearInterval(interval)
                    return
                }
            }, 500)

        })
    }

    stringify(data: any) {
        return JSON.stringify(data);
    }

    parse(data: string) {
        let object = JSON.parse(data);

        for(let x in object) {
            object[x].bid = new BigNumber(object[x].bid)
            object[x].ask = new BigNumber(object[x].ask)
        }

        return object
    }

    tickersFor(exchangeID: string, symbols: any){
        this.setupSocket(exchangeID)
        let promises: Promise<any>[] = [];
        symbols.forEach((s:any)=>{
            promises.push(this.tickerFor(exchangeID, s.symbol))
        })

        return Promise.all(promises).then((results)=>{
            let resp: any = {}
            results.forEach((r)=>{
                resp[r.symbol] = r.ticker
            })

            return resp
        })
    }

    setupSocket(exchangeID: string){
        switch(exchangeTypeLookup[exchangeID]){
            case BinanceUS:
                this.setupBinanceSocket()
                break
            case Gemini:
                this.setupGeminiSocket().then()
                break
            case CoinbasePro:
                this.setupCoinbaseSocket().then()
                break
            case Kraken:
                this.setupKrakenSocket(exchangeID).then()
                break
            default:
                break
        }
    }

    /*
    as a binance listener I want to get notified of symbols that need to be subscribed to.
       - Acceptance Criteria
           - when socket is initialized .. we should simply wait for our first event .. before connecting to the websocket

     */

    setupBinanceSocket(){
        let name = BinanceUS
        if(this.initialized[name]){
            return
        }
        let errored = false
        this.initialized[name] = true
        let subscribed: Record<string, any> = {}
        let initialized = false
        let setup: any;
        let subscribeEmitter = new EventEmitter()
        const subEvent = "subscribe"
        let onEvent = (symbol: any) => {
            if(subscribed[symbol]){
                return
            }
            if(errored){
                stopListening()
                return;
            }
            if(!initialized){
                setup()
            }

            subscribeEmitter.emit(subEvent, symbol)
            subscribed[symbol] = true
        }
        this.emitters[name].on(tickerEventType, onEvent)
        let stopListening = ()=>{
            errored = true
            this.emitters[name].off(tickerEventType, onEvent)
        }

        setup = ()=>{
            initialized = true
            this.tickers[name] = {}
            this.sockets[name] = new WebSocket('wss://stream.binance.us:9443/stream?streams=btcusd@miniTicker')
            let sock = this.sockets[name]

            let waitTillConnected = ()=>{
                return new Promise((resolve, reject)=>{
                    let check = ()=>{
                        return sock.readyState == WebSocket.OPEN
                    }
                    if(check()){
                        resolve(true)
                        return
                    }

                    let it = setInterval(()=>{
                        let valid = check()
                        let failed = sock.readyState == WebSocket.CLOSED
                        if(valid || failed){
                            clearInterval(it)


                            failed ? reject(true) : resolve(true)
                        }
                    }, 100)
                })
            }
            let ids = 0;
            let messagesToSend: any[] = [];
            let callSend = debounce(async()=>{
                if(!messagesToSend.length){
                    return
                }
                ids++
                sock.send(JSON.stringify({
                    method: "SUBSCRIBE",
                    params: messagesToSend,
                    id: ids,
                }))
                messagesToSend = []

            }, 200)

            subscribeEmitter.on(subEvent, async (symbol)=>{
                try{
                    messagesToSend.push(`${symbol.toLowerCase()}@bookTicker`)
                    await waitTillConnected()
                    await callSend()
                }catch (e){}

            })

            sock.onmessage = (ev)=>{
                let result = JSON.parse(ev.data)

                if(!(result?.stream && result.stream.match(/bookTicker/g))){
                    return
                }

                let m = result.data
                this.tickers[name][m.s]= {
                    bid: new BigNumber(m.a),
                    ask: new BigNumber(m.b),
                }
            }


            sock.onerror = (err)=>{
                console.log("an error occurred", err)
                try{
                    stopListening()
                    sock.close()
                }catch (e){}

            }

            sock.onclose = (ev)=>{
                console.log('closed', ev)
                stopListening()
                setTimeout(()=>{
                    delete this.sockets[name]
                    delete this.initialized[name]
                    this.setupBinanceSocket()
                }, 15000)
            }
        }
    }

    async setupGeminiSocket(){
        let name = Gemini
        if(this.initialized[name]){
            return
        }
        this.initialized[name] = true

        if(!this.tickers[name]){
            this.tickers[name] = {}
        }


       let books: Record<string, OrderBook> = {}
        let result = await geminiAPI.get('/v1/symbols')
        let symbols: any = []
        result?.data?.forEach?.((r: any)=>{
            symbols.push(r.toUpperCase())
        })

        this.sockets[name] = new WebSocket('wss://api.gemini.com/v2/marketdata')
        let sock = this.sockets[name]
        sock.onopen = ()=>{
            sock.send(JSON.stringify({
                "type": "subscribe",
                "subscriptions":[
                    {"name":"l2","symbols": symbols}]}))
        }

        sock.onmessage = (ev)=>{
            let data = JSON.parse(ev.data)
            if(data.type != "l2_updates"){
                return
            }
            let symbol = this.toSymbol(data.symbol)
            if(!books[symbol]){
                books[symbol] = new OrderBook(10)
            }
            let book = books[symbol]
            data.changes.forEach((c: any)=>{
                let side: OrderBookSide = c[0] == "buy" ? OrderBookSide.Bid : OrderBookSide.Ask
                if(c[2]){
                    book.add( side, c[1], c[2])
                } else {
                    book.remove( side, c[1],)
                }
            })

            let ticker = book.ticker()
            this.tickers[name][symbol] = ticker
        }

        sock.onerror = (err)=>{
            try{
                sock.close()
            }catch (e){}
            setTimeout(()=>{
                delete this.sockets[name]
                this.setupGeminiSocket()
            }, 3000)
        }
    }

    async setupCoinbaseSocket(){
        let name = CoinbasePro
        if(this.initialized[name]){
            return
        }
        this.initialized[name] = true

        if(!this.tickers[name]){
            this.tickers[name] = {}
        }

        let result = await coinbaseApi.get('/products')
        let symbols: any = []
        let relatedSymbols: any = {}
        result?.data?.forEach?.((r: any)=>{
            symbols.push(r.id)
            let symbol = this.toSymbol(r.id)
            relatedSymbols[symbol] = [
                symbol,
                r.id]
        })

        this.sockets[name] = new WebSocket('wss://ws-feed.pro.coinbase.com')
        let sock = this.sockets[name]
        sock.onopen = ()=>{
            sock.send(JSON.stringify({
                "type": "subscribe",
                "product_ids": symbols,
                "channels": ["ticker"]
            }))
        }

        sock.onmessage = (ev)=>{
            let data = JSON.parse(ev.data)
            if(data.type != "ticker"){
                return
            }
            let symbol = this.toSymbol(data.product_id)
            relatedSymbols[symbol]?.forEach?.((symbol: any)=>{
                this.tickers[name][symbol] = {
                    bid: new BigNumber(data.best_bid),
                    ask: new BigNumber(data.best_ask)
                }
            })
        }

        sock.onerror = (err)=>{
            try{
                sock.close()
            }catch (e){}
            setTimeout(()=>{
                delete this.sockets[name]
                this.setupCoinbaseSocket().then()
            }, 3000)
        }
    }

    async setupKrakenSocket(exchangeID: any){
        let name = Kraken
        if(this.initialized[name]){
            return
        }
        this.initialized[name] = true

        if(!this.tickers[name]){
            this.tickers[name] = {}
        }

        let result = await this.traderSVC.listSymbols(exchangeID)
        let symbols: any = []
        let relatedSymbols: any = {}
        result?.forEach?.((r: any)=>{
            symbols.push(r.websocket_symbol)
            let symbol = this.toSymbol(r.websocket_symbol)
            relatedSymbols[symbol] = [
                symbol,
                r.websocket_symbol, r.symbol, r.base + r.quote]
        })

        this.sockets[name] = new WebSocket('wss://ws.kraken.com')
        let sock = this.sockets[name]
        sock.onopen = ()=>{
            sock.send(JSON.stringify({
                "event": "subscribe",
                "pair": symbols,
                "subscription": {
                    name: "ticker"
                }
            }))
        }

        sock.onmessage = (ev)=>{
            let arr = JSON.parse(ev.data)
            if(arr[2] != "ticker"){
                return
            }
            let symbol = this.toSymbol(arr[3])
            let data = arr[1]
            relatedSymbols[symbol]?.forEach?.((symbol: any)=>{
                this.tickers[name][symbol] = {
                    bid: new BigNumber(data.b[0]),
                    ask: new BigNumber(data.a[0])
                }
            })

        }

        sock.onerror = (err)=>{
            try{
                sock.close()
            }catch (e){}
            setTimeout(()=>{
                delete this.sockets[name]
                this.setupKrakenSocket(exchangeID).then()
            }, 3000)
        }
    }

    cleanup(){
        Object.values(this.sockets).forEach((socket)=>{
            socket.close()
        })
        this.sockets = {}
        this.tickers = {}
        this.initialized = {}
    }

}

function debounce<F extends (...params: any[]) => void>(fn: F, delay: number) {
    let timeoutID: number;
    return function(this: any, ...args: any[]) {
        clearTimeout(timeoutID);
        timeoutID = window.setTimeout(() => fn.apply(this, args), delay);
    } as F;
}
